import { reportApiError } from "@services/error-reporting";
import { useApiLoadingStore } from "@stores/api-loading.store";
import { authRequestIdentity } from "@stores/auth-session";
import { useAuthStore } from "@stores/auth.store";
import { useConnectivity } from "@stores/connectivity.store";
import { isNetworkError } from "../../offline/session";
import { OFFLINE_HEADER, SAVED_AT_HEADER } from "../../offline/sw-routes";
import axios, {
  AxiosError,
  AxiosInstance,
  CanceledError,
  InternalAxiosRequestConfig,
} from "axios";

interface SessionRequest extends InternalAxiosRequestConfig {
  _authSession?: string;
  _authEpoch?: number;
  _retry?: boolean;
  _mutationTracked?: boolean;
}

const MUTATION_METHODS = new Set(["post", "put", "patch", "delete"]);
const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";
const SUPER_ADMIN_SYNTHETIC_TENANT_ID = "00000000-0000-0000-0000-000000000000";
const AUTH_PATHS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/forgot-password",
  "/auth/reset-password",
  // Sign-in steps: a 401 there is a wrong code or a spent pass, not an expired session.
  "/auth/mfa/",
  "/auth/sso/",
];

function shouldSkipRefresh(path?: string): boolean {
  return AUTH_PATHS.some((authPath) => path?.includes(authPath));
}

/** Uploads of up to 500 MB take a while on a slow connection; the server answers once it has the file. */
export const UPLOAD_TIMEOUT_MS = 60 * 60_000;

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

function resolveTenantHeader(
  user?: { tenantId?: string; role?: string | null } | null,
): string | undefined {
  if (user?.tenantId) {
    return user.role === "SUPER_ADMIN" &&
      user.tenantId === SUPER_ADMIN_SYNTHETIC_TENANT_ID
      ? undefined
      : user.tenantId;
  }
  if (typeof window === "undefined") return undefined;
  let persistedTenantId: string | undefined;
  try {
    persistedTenantId = window.localStorage
      .getItem("fh:last-tenant-id")
      ?.trim();
    if (persistedTenantId === SUPER_ADMIN_SYNTHETIC_TENANT_ID) {
      window.localStorage.removeItem("fh:last-tenant-id");
      persistedTenantId = undefined;
    }
  } catch {
    /* Domain resolution is still available when storage is disabled. */
  }
  if (persistedTenantId) return persistedTenantId;
  const hostname = window.location.hostname.trim().toLowerCase();
  if (
    !hostname ||
    hostname === "localhost" ||
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)
  )
    return undefined;
  const reserved = (label: string) =>
    ["localhost", "www", "api", "app"].includes(label);
  if (hostname.endsWith(".localhost")) {
    const subdomain = hostname.replace(/\.localhost$/, "");
    return subdomain && !reserved(subdomain) ? subdomain : undefined;
  }
  const parts = hostname.split(".").filter(Boolean);
  return parts.length >= 3 && !reserved(parts[0]) ? parts[0] : undefined;
}

const belongsToCurrentSession = (config: SessionRequest) =>
  config._authSession === authRequestIdentity(useAuthStore.getState());
function canceledSession(config: SessionRequest) {
  const error = new CanceledError("Authentication session changed");
  error.config = config;
  return error;
}

function finishMutation(config?: SessionRequest) {
  if (config?._mutationTracked) {
    config._mutationTracked = false;
    useApiLoadingStore.getState()._decrement();
  }
}

// Capture identity synchronously at invocation, before a caller can change accounts.
// Token renewal happens on 401 (and through the session renewal prompt). An async
// request interceptor could silently attach a new account's token to an old command.
apiClient.interceptors.request.use(
  (config: SessionRequest) => {
    const state = useAuthStore.getState();
    config._authSession ??= authRequestIdentity(state);
    config._authEpoch ??= state.sessionEpoch;
    if (!belongsToCurrentSession(config)) throw canceledSession(config);
    if (state.tokens?.accessToken)
      config.headers.Authorization = `Bearer ${state.tokens.accessToken}`;
    else delete config.headers.Authorization;
    const tenant = resolveTenantHeader(state.user);
    if (tenant) config.headers["X-Tenant-ID"] = tenant;
    else delete config.headers["X-Tenant-ID"];
    if (MUTATION_METHODS.has((config.method ?? "").toLowerCase())) {
      config._mutationTracked = true;
      useApiLoadingStore.getState()._increment();
    }
    return config;
  },
  (error) => {
    throw error;
  },
  { synchronous: true },
);

let invalidatedEpoch: number | null = null;

async function logoutAndRedirect(epoch: number, reason?: string) {
  if (useAuthStore.getState().sessionEpoch !== epoch) return;
  invalidatedEpoch = epoch;
  if (reason) {
    try {
      window.sessionStorage.setItem("auth:logoutReason", reason);
    } catch {
      /* Storage is optional. */
    }
  }
  const logoutEpoch = epoch + 1;
  await useAuthStore.getState().logout();
  // A newer login may finish while server logout is pending. Never redirect it.
  if (
    useAuthStore.getState().sessionEpoch === logoutEpoch &&
    !useAuthStore.getState().isAuthenticated
  ) {
    const publicPaths = ["/login", "/forgot-password"];
    if (!publicPaths.some((path) => window.location.pathname.startsWith(path)))
      window.location.href = "/login";
  }
}

/** Shown for a change that needs the server while it cannot be reached. */
export const OFFLINE_CHANGE_MESSAGE =
  "You're offline. This change needs a connection; try again when you're back online.";

apiClient.interceptors.response.use(
  (response) => {
    const config = response.config as SessionRequest;
    finishMutation(config);
    const savedAt = response.headers[SAVED_AT_HEADER.toLowerCase()] as string | undefined;
    if (savedAt) useConnectivity.getState().unreachable(savedAt);
    else useConnectivity.getState().reached();
    if (!belongsToCurrentSession(config)) throw canceledSession(config);
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as SessionRequest | undefined;
    finishMutation(config);
    if (axios.isCancel(error)) return Promise.reject(error);
    if (config && !belongsToCurrentSession(config))
      return Promise.reject(canceledSession(config));
    if (config?._authEpoch === invalidatedEpoch) return Promise.reject(error);
    // Not reaching the server is expected in the field: no error report, no sign-out.
    if (isNetworkError(error) || error.response?.headers?.[OFFLINE_HEADER.toLowerCase()]) {
      useConnectivity.getState().unreachable();
      // A page not opened online on this device: show the worker's plain explanation.
      const saved = (error.response?.data as { message?: unknown } | undefined)?.message;
      if (typeof saved === "string") error.message = saved;
      if (isNetworkError(error) && config && MUTATION_METHODS.has((config.method ?? "").toLowerCase())) {
        (error as AxiosError & { offline: boolean }).offline = true;
        error.message = OFFLINE_CHANGE_MESSAGE;
      }
      return Promise.reject(error);
    }
    if (error.response) useConnectivity.getState().reached();
    reportApiError(error);

    const responseData = error.response?.data as
      | { code?: string; message?: string | string[] }
      | undefined;
    const message = Array.isArray(responseData?.message)
      ? responseData.message.join(" ")
      : responseData?.message;
    const tenantContextError =
      error.response?.status === 403 &&
      message?.includes("Tenant context could not be resolved");
    if (config && !shouldSkipRefresh(config.url)) {
      if (
        error.response?.status === 401 &&
        ["SESSION_REPLACED", "TOKEN_EXPIRED"].includes(responseData?.code ?? "")
      ) {
        await logoutAndRedirect(
          config._authEpoch!,
          responseData?.code === "SESSION_REPLACED"
            ? "session_replaced"
            : undefined,
        );
        return Promise.reject(error);
      }
      if (
        (error.response?.status === 401 || tenantContextError) &&
        !config._retry
      ) {
        config._retry = true;
        try {
          // The store shares renewal only within this session generation.
          await useAuthStore.getState().refreshToken();
          if (!belongsToCurrentSession(config)) throw canceledSession(config);
          return apiClient(config);
        } catch (refreshError) {
          if (useAuthStore.getState().sessionEpoch !== config._authEpoch)
            return Promise.reject(canceledSession(config));
          if (isNetworkError(refreshError)) return Promise.reject(refreshError);
          await logoutAndRedirect(config._authEpoch!);
          return Promise.reject(refreshError);
        }
      }
    }

    if (error.response?.status === 409) {
      (error as AxiosError & { isConflict: boolean }).isConflict = true;
    }
    return Promise.reject(error);
  },
);

export { apiClient };
export default apiClient;

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((res) => res.data),
  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then((res) => res.data),
  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then((res) => res.data),
  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then((res) => res.data),
  delete: <T>(url: string) => apiClient.delete<T>(url).then((res) => res.data),
  upload: <T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient
      .post<T>(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (onProgress && event.total)
            onProgress(Math.round((event.loaded * 100) / event.total));
        },
      })
      .then((res) => res.data);
  },
};
